import { Component, input, signal, effect, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { TicketClosingField, getAccentIconClasses, getAccentIconColor, AccentColor, DataRowComponent, TicketDetailStore } from '../../../../shared';

interface ClosingFieldSection {
  title: string;
  icon: string;
  accentColor: AccentColor;
  fields: { key: string; label: string; value: string }[][];
}

@Component({
  selector: 'app-questionnaire',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslatePipe, DataRowComponent],
  templateUrl: './questionnaire.component.html',
})
export class QuestionnaireComponent {
  private store = inject(TicketDetailStore);

  public ticketId = input.required<number>();
  public closingFields = computed<TicketClosingField[]>(() => this.store.closingFields());

  public loading = input<boolean>(false);

  public sections = signal<ClosingFieldSection[]>([]);
  public expandedSections = signal<Set<string>>(new Set());
  public allExpanded = signal(true);

  constructor() {

    effect(() => {
      const fields = this.closingFields();
      this.loadSections(fields);
    });
  }

  public toggleAllSections(): void {
    if (this.allExpanded()) {
      // Colapsar todas
      this.expandedSections.set(new Set());
      this.allExpanded.set(false);
    } else {
      // Expandir todas
      this.expandedSections.set(new Set(this.sections().map(s => s.title)));
      this.allExpanded.set(true);
    }
  }

  public toggleSection(title: string): void {
    const current = new Set(this.expandedSections());
    if (current.has(title)) {
      current.delete(title);
    } else {
      current.add(title);
    }
    this.expandedSections.set(current);
  }

  public isExpanded(title: string): boolean {
    return this.expandedSections().has(title);
  }

  public getIconClasses(section: ClosingFieldSection): string {
    return getAccentIconClasses(section.accentColor);
  }

  public getIconColor(section: ClosingFieldSection): string {
    return getAccentIconColor(section.accentColor);
  }

  public formatValue(value: string | number | boolean | undefined): string {
    if (value === undefined || value === null) return '-';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'number') {
      return value.toLocaleString('es-MX');
    }
    return String(value);
  }

  private getSectionColor(index: number): AccentColor {
    const colors: AccentColor[] = ['primary', 'success', 'warning', 'danger', 'info', 'violet'];
    return colors[index % colors.length];
  }

  private loadSections(fields: TicketClosingField[]): void {
    if (!fields || fields.length === 0) {
      this.sections.set([]);
      return;
    }

    const groupedByCategory = new Map<string, TicketClosingField[]>();
    fields.forEach(field => {
      if (field.ComponentType !== 'File') {
        const category = field.ClosingFieldCategory || 'General';
        if (!groupedByCategory.has(category)) {
          groupedByCategory.set(category, []);
        }
        groupedByCategory.get(category)!.push(field);
      }
    });

    const closingFieldSections: ClosingFieldSection[] = [];
    groupedByCategory.forEach((fieldGroup, category) => {
      fieldGroup.sort((a, b) => a.Order - b.Order);

      const midPoint = Math.ceil(fieldGroup.length / 2);
      const column1 = fieldGroup.slice(0, midPoint).map(f => ({
        key: String(f.Id),
        label: f.Label || f.ClosingFieldDescription || '',
        value: f.Value || '',
      }));
      const column2 = fieldGroup.slice(midPoint).map(f => ({
        key: String(f.Id),
        label: f.Label || f.ClosingFieldDescription || '',
        value: f.Value || '',
      }));

      closingFieldSections.push({
        title: category,
        icon: 'folder',
        accentColor: this.getSectionColor(closingFieldSections.length),
        fields: [column1, column2],
      });

    });

    this.sections.set(closingFieldSections);

    // Expande todas las secciones por defecto
    this.expandedSections.set(new Set(closingFieldSections.map(s => s.title)));
    this.allExpanded.set(true);
  }

}
